import React, { useEffect, useState, useCallback, useRef } from 'react';
import { CircleStackIcon } from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { collection, query, where, orderBy, getDocs, limit, startAfter } from 'firebase/firestore';
import { db, auth } from '../../config/firebase';
import { useDummyData } from '../../contexts/DummyDataContext';

const PAGE_SIZE = 10;

export default function ActivityFeedPage() {
  const [activities, setActivities] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [totalPages, setTotalPages] = useState(null);
  const cursorStack = useRef([]);
  const allDocsCache = useRef(null); // fallback: in-memory pagination
  const { isDummyDataEnabled } = useDummyData();

  const paginateFromCache = useCallback((pageIndex) => {
    const all = allDocsCache.current || [];
    const start = pageIndex * PAGE_SIZE;
    const pageSlice = all.slice(start, start + PAGE_SIZE);
    setActivities(pageSlice);
    setHasNext(start + PAGE_SIZE < all.length);
    setPage(pageIndex);
    setIsLoading(false);
  }, []);

  const fetchPage = useCallback(async (cursor = null, pageIndex = 0) => {
    if (!auth.currentUser) return;
    setIsLoading(true);
    setError(null);

    try {
      if (isDummyDataEnabled) {
        setActivities([]);
        setHasNext(false);
        setIsLoading(false);
        return;
      }

      // If we already fell back to in-memory mode, paginate from cache
      if (allDocsCache.current) {
        paginateFromCache(pageIndex);
        return;
      }

      const col = collection(db, 'transactions');
      const userFilter = where('userId', '==', auth.currentUser.uid);
      const tsOrder = orderBy('timestamp', 'desc');
      const pageLimit = limit(PAGE_SIZE + 1);

      let snapshot;
      try {
        const q = cursor
          ? query(col, userFilter, tsOrder, startAfter(cursor), pageLimit)
          : query(col, userFilter, tsOrder, pageLimit);
        snapshot = await getDocs(q);
        console.log('[ActivityFeed] Server-side pagination active, fetched', snapshot.docs.length, 'docs for page', pageIndex);
      } catch (err) {
        if (err.code === 'failed-precondition') {
          console.warn('[ActivityFeed] Index not ready, falling back to in-memory pagination');
          const fbQ = query(col, userFilter, limit(500));
          const fbSnap = await getDocs(fbQ);
          const sorted = [...fbSnap.docs]
            .sort((a, b) => {
              const ta = a.data().timestamp?.toMillis?.() || 0;
              const tb = b.data().timestamp?.toMillis?.() || 0;
              return tb - ta;
            })
            .map(doc => ({
              id: doc.id,
              ...doc.data(),
              timestamp: doc.data().timestamp?.toDate(),
            }));
          allDocsCache.current = sorted;
          setTotalPages(Math.ceil(sorted.length / PAGE_SIZE));
          paginateFromCache(0);
          return;
        }
        throw err;
      }

      const docs = snapshot.docs;
      const hasMore = docs.length > PAGE_SIZE;
      const pageDocs = hasMore ? docs.slice(0, PAGE_SIZE) : docs;

      const activitiesData = pageDocs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate(),
        _snap: doc,
      }));

      setActivities(activitiesData);
      setHasNext(hasMore);
      setPage(pageIndex);

      // On first page load, do a one-time count to get total pages
      if (pageIndex === 0 && totalPages === null) {
        try {
          const countQ = query(col, userFilter);
          const countSnap = await getDocs(countQ);
          setTotalPages(Math.ceil(countSnap.size / PAGE_SIZE));
        } catch (_) { /* non-critical */ }
      }
    } catch (err) {
      console.error('Error loading activities:', err);
      setError('Failed to load activities. Please try again later.');
      setActivities([]);
    } finally {
      setIsLoading(false);
    }
  }, [isDummyDataEnabled, paginateFromCache]);

  useEffect(() => {
    cursorStack.current = [];
    allDocsCache.current = null;
    fetchPage(null, 0);
  }, [fetchPage]);

  const goNext = () => {
    if (!hasNext || activities.length === 0) return;
    if (allDocsCache.current) {
      paginateFromCache(page + 1);
      return;
    }
    const lastDoc = activities[activities.length - 1]._snap;
    cursorStack.current[page] = lastDoc;
    fetchPage(lastDoc, page + 1);
  };

  const goPrev = () => {
    if (page <= 0) return;
    if (allDocsCache.current) {
      paginateFromCache(page - 1);
      return;
    }
    const prevPage = page - 1;
    const cursor = prevPage > 0 ? cursorStack.current[prevPage - 1] : null;
    fetchPage(cursor, prevPage);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-6xl">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-700">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-2 text-sm text-red-600 hover:text-red-500"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Activity Feed</h1>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
        <table className="w-full">
          <thead>
            <tr className="text-left text-gray-500 text-sm bg-gray-50 border-b border-gray-200">
              <th className="px-6 py-4 font-medium">Action</th>
              <th className="px-6 py-4 font-medium">Date</th>
              <th className="px-6 py-4 font-medium">Type</th>
              <th className="px-6 py-4 font-medium">Credits</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {activities.map((activity) => (
              <tr key={activity.id}>
                <td className="px-6 py-4 text-gray-900">
                  {activity.action === 'image_generation'
                    ? 'Image Generation'
                    : activity.action === 'image_generation_refund'
                      ? 'Image Generation Refund'
                      : activity.action === 'verification_bonus'
                        ? 'Verification Reward'
                        : activity.action === 'coupon_redemption'
                          ? `Coupon Claimed${activity.couponCode ? ` (${activity.couponCode})` : ''}`
                          : 'Credit Purchase'}
                </td>
                <td className="px-6 py-4 text-gray-500">
                  {format(activity.timestamp, 'MMM dd, yyyy HH:mm')}
                </td>
                <td className="px-6 py-4">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    activity.type === 'debit' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                  }`}>
                    {activity.type === 'debit' ? 'Debit' : 'Credit'}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <span className={activity.type === 'credit' ? 'text-green-600' : 'text-red-600'}>
                    <CircleStackIcon className="inline-block h-4 w-4 mr-1" />
                    {activity.type === 'credit' ? '+' : '-'}{Math.abs(activity.amount)}
                  </span>
                </td>
              </tr>
            ))}
            {activities.length === 0 && (
              <tr>
                <td colSpan={4} className="px-6 py-8 text-center text-gray-500">
                  No activity yet
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {(page > 0 || hasNext) && (
        <div className="flex items-center justify-between mt-4">
          <button
            onClick={goPrev}
            disabled={page === 0}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <span className="text-sm text-gray-500">
            Page {page + 1}{totalPages !== null ? ` of ${totalPages}` : ''}
          </span>
          <button
            onClick={goNext}
            disabled={!hasNext}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}